import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataTableSetSorting, DataTableSorting } from "@/components/molecules";

type SortDirection = "asc" | "desc";

interface UseSortingOptions {
  sort?: string;
  direction?: SortDirection;
  /**
   * Remember this table's sort between sessions, under this localStorage key.
   *
   * Opt-in, and named by the caller, because this hook is shared by ~48 tables — the same shape as
   * `usePagination`'s page-size persistence. Pass an operator-scoped key (`{operatorId}:…`) unless the
   * preference genuinely should follow the user across operators; `usePagination` keys on a bare name
   * and consequently leaks page size between them.
   */
  name?: string;
}

type StoredSort = { sort: string; direction: SortDirection };

const readStoredSort = (name: string): StoredSort | undefined => {
  try {
    const raw = window.localStorage.getItem(name);
    if (!raw) return undefined;

    const stored = JSON.parse(raw) as StoredSort;
    return stored.sort && (stored.direction === "asc" || stored.direction === "desc") ? stored : undefined;
  } catch {
    // Unreadable or hand-edited storage falls back to the caller's default, never breaks the table.
    return undefined;
  }
};

const writeStoredSort = (name: string, stored: StoredSort | null) => {
  try {
    if (stored) window.localStorage.setItem(name, JSON.stringify(stored));
    else window.localStorage.removeItem(name);
  } catch {
    // Private-mode / quota failures just mean the choice doesn't outlive the tab.
  }
};

//TODO: implement use-query-param library
export const useSorting = ({ sort: defaultSort, direction: defaultDirection, name }: UseSortingOptions = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Read once per mount rather than on every render, so every `useSorting()` in a page agrees on the
   * restored value even though each holds its own instance (the bookings page has two: the table and
   * its params hook).
   */
  const [storedSort] = useState(() => (name ? readStoredSort(name) : undefined));

  // A URL that carries a sort wins: a shared or bookmarked link has to survive, and clicking one
  // should not be silently rewritten into whatever the last session happened to be sorted by.
  const sort = searchParams.get("sort") ?? storedSort?.sort ?? defaultSort ?? "";
  const direction = searchParams.get("direction") ?? storedSort?.direction ?? defaultDirection ?? "";
  const sorting: DataTableSorting = sort && direction ? [{ id: sort, desc: direction === "desc" }] : [];

  const setSorting: DataTableSetSorting = (updaterOrValue) => {
    let newSorting: DataTableSorting = [];

    if (typeof updaterOrValue === "function") {
      newSorting = updaterOrValue(sorting);
    } else {
      newSorting = updaterOrValue;
    }

    if (newSorting.length < 1) {
      searchParams.delete("sort");
      searchParams.delete("direction");
      // Forget it too, or clearing the sort would resurrect the stored one on the next load.
      if (name) writeStoredSort(name, null);
    } else {
      const nextSort = newSorting[0].id ?? "";
      const nextDirection: SortDirection = newSorting[0].desc ? "desc" : "asc";

      searchParams.set("sort", nextSort);
      searchParams.set("direction", nextDirection);
      if (name) writeStoredSort(name, { sort: nextSort, direction: nextDirection });
    }
    setSearchParams(searchParams);
  };

  return {
    sorting,
    setSorting,
    sortKey: sort,
    direction,
  };
};
